import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { LocalOverrides } from '@atlaskit/feature-gate-js-client';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import {
	processQueryParamFeatureGates,
	processQueryParamExperiments,
} from '@confluence/feature-flag-url-override';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs';
import { getLogger } from '@confluence/logger';

const STATSIG_OVERRIDES_STORAGE_KEY = PERSISTED_KEYS_ON_SERVER?.PERSISTED_OVERRIDES_STATSIG;
const logger = getLogger('persistedStatsigOverrides');

export const updatePersistedStatsigOverrides = (overrides: LocalOverrides) => {
	if (confluenceLocalStorageInstance) {
		confluenceLocalStorageInstance.setItem(STATSIG_OVERRIDES_STORAGE_KEY, overrides);
	}
};

export const applyPersistedStatsigOverrides = (additionalOverrides?: Partial<LocalOverrides>) => {
	let overrides: Record<string, string> | undefined;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		// On server overrides are provided by cc-frontend-ssr or bifrost
		overrides = getSSRRenderInputs().persistedStatsigOverrides;
	} else if (window.__SSR_RENDERED__) {
		// When hydrate overrides are provided by server
		overrides = window?.__STATSIG_BOOTSTRAP__?.persistedStatsigOverrides;
	} else {
		// Otherwise get from persisted storage
		if (confluenceLocalStorageInstance) {
			overrides = confluenceLocalStorageInstance.getItem(STATSIG_OVERRIDES_STORAGE_KEY);
		}
	}

	let configs = overrides?.configs || {};
	let gates = overrides?.gates || {};
	let layers = overrides?.layers || {};

	// On client-side PlayWright have client only overrides
	try {
		const clientOnlyOverrides = JSON.parse(
			window.sessionStorage.getItem('clientOnlyOverrides') || '{}',
		);
		if (clientOnlyOverrides) {
			configs = { ...configs, ...clientOnlyOverrides?.configs };
			gates = { ...gates, ...clientOnlyOverrides?.gates };
			layers = { ...layers, ...clientOnlyOverrides?.layers };
		}
	} catch {}

	// Finally user can override with ?fg=fg1:value,fg2:value
	// Server will handle fg query and pass along in persistedStatsigOverrides
	// Here we are only handling no-SSR case
	const urlOverrideGates = processQueryParamFeatureGates();
	if (urlOverrideGates) {
		gates = { ...gates, ...urlOverrideGates };
	}

	// User can override experiments with ?exp=experimentName:value (defaults to isEnabled property)
	// or ?exp=experimentName:property:value
	const urlOverrideExperiments = processQueryParamExperiments();
	if (urlOverrideExperiments) {
		configs = { ...configs, ...urlOverrideExperiments };
	}

	if (additionalOverrides) {
		gates = { ...gates, ...additionalOverrides?.gates };
		configs = { ...configs, ...additionalOverrides?.configs };
		layers = { ...layers, ...additionalOverrides?.layers };
	}

	FeatureGates.setOverrides({ configs, layers, gates });
	logger.info`Statsig overrides applied: ${{ configs, layers, gates }}`;
};
